/* ==========================================================================
   SHIRTPLUS — SP-800-027: AVIS-Warenkorb-Integritaet & Mengensynchronisierung
   ==========================================================================

   ZIEL
   Haelt die Menge von AVIS-Aufpreispositionen (z. B. "Stick auf
   Latzschuerzen") synchron mit der Summe der Mengen aller zugehoerigen
   Hauptprodukt-Zeilen im Warenkorb. Entfernt verwaiste Aufpreispositionen,
   wenn das zugehoerige Hauptprodukt vollstaendig aus dem Warenkorb entfernt
   wurde. Greift NICHT in das Premium Add-to-Cart Modal, die AVIS-App, die
   MinCart-App oder Produktpreise ein - reine Warenkorb-Nachbearbeitung ueber
   die oeffentliche Shopify Ajax Cart API (/cart.js, /cart/change.js).

   GRUNDLAGE DER ZUORDNUNG (siehe assets/sp-pdp.js, collectItems()/
   collectAvisCharges(), SP-800-027-Ergaenzung)
   Jede Add-to-Cart-Aktion mit AVIS-Eigenschaften vergibt eine pro Klick neu
   erzeugte, eindeutige interne Kennung als Line-Item-Property
   `_sp_parent_id` - identisch auf allen Hauptprodukt-Zeilen UND allen dazu
   gehoerenden Aufpreis-Zeilen dieses einen Add-to-Cart-Aufrufs. Ein
   zusaetzliches Flag `_sp_role` ("main" | "charge") unterscheidet die Rolle
   je Zeile. Beide Property-Namen beginnen mit "_" - Shopify blendet solche
   Properties nativ in Warenkorb, Checkout, Bestellstatusseite und
   Bestellbestaetigungs-Mail aus; zusaetzlich filtert snippets/line-item.liquid
   des Themes sie ebenfalls explizit heraus. Fuer Kunden zu keinem Zeitpunkt
   sichtbar. Diese Kennung - nicht Produkttitel, nicht sichtbare Reihenfolge,
   nicht die naechste Warenkorbzeile - ist die einzige Grundlage der
   Gruppierung in diesem Skript.

   Warenkoerbe, die VOR diesem Fix befuellt wurden (keine `_sp_parent_id`
   vorhanden), werden von diesem Skript nicht angefasst - kein rueckwirkender
   Eingriff in bestehende, nicht taggierte Warenkorbzeilen.

   MENGENLOGIK
   Fuer jede Gruppe (gleiche `_sp_parent_id`):
     Ziel-Menge = Summe der Mengen aller Zeilen mit role "main"
     Jede Zeile mit role "charge", deren Menge davon abweicht, wird per
     /cart/change.js auf die Ziel-Menge korrigiert.
   Das deckt sowohl den einfachen Fall (1 Hauptprodukt-Zeile + 1
   Aufpreis-Zeile) als auch Mehrfach-Varianten in einem Add-to-Cart-Aufruf
   (z. B. zwei Groessen in einem Klick, EINE gemeinsame Aufpreis-Zeile) korrekt
   ab, da collectAvisCharges() in sp-pdp.js die Aufpreismenge bereits als
   Summe aller Hauptprodukt-Mengen dieses Aufrufs bildet.

   ENTFERNEN-LOGIK
   Existieren in einer Gruppe nur noch Zeilen mit role "charge" (also keine
   "main"-Zeile mehr - das Hauptprodukt wurde vollstaendig entfernt), werden
   alle verbleibenden Aufpreis-Zeilen dieser Gruppe automatisch auf Menge 0
   gesetzt (= entfernt). Wird umgekehrt NUR eine Aufpreis-Zeile entfernt,
   bleiben Hauptprodukt und andere Aufpreis-Zeilen unangetastet - das ist vom
   Kunden bewusst so waehlbar (Ticket Abschnitt 7).

   AUSLOESER
   - `cart:change` (vom Theme nativ dispatched nach Mengenaenderungen ueber
     Cart Drawer, Warenkorbseite, Plus-/Minus-Buttons und direkte
     Mengeneingabe - siehe assets/theme.js, `_onCartChangedListener`).
   - Seitenladen/Reload (einmaliger Konsistenz-Check, deckt u. a. den Fall ab,
     dass eine Mengenaenderung ueber einen vollstaendigen Seiten-Reload
     (klassisches Formular-"Aktualisieren") statt per AJAX erfolgte).

   KEINE ENDLOSSCHLEIFE
   `isSyncing`-Flag verhindert überlappende Korrekturlaeufe. Die eigenen
   Korrektur-Aufrufe laufen als direkter fetch() an /cart/change.js (nicht
   ueber die Theme-eigene <line-item-quantity>-Komponente) und loesen dadurch
   selbst kein neues `cart:change`-Event aus - kein rekursiver Trigger. Nach
   Abschluss wird `cart:refresh` dispatched (dasselbe, bereits in sp-pdp.js
   verwendete Signal), damit Cart Drawer und Warenkorbseite die korrigierten
   Mengen/Preise aus dem Server-Zustand neu rendern.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__spCartIntegrityLoaded) return;
  window.__spCartIntegrityLoaded = true;

  var PARENT_KEY = '_sp_parent_id';
  var ROLE_KEY = '_sp_role';
  var isSyncing = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchCart() {
    return fetch(root() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function changeLine(key, quantity) {
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  // Gruppiert ausschliesslich Zeilen, die tatsaechlich eine `_sp_parent_id`
  // tragen. Zeilen ohne diese Property (aeltere Warenkoerbe, Fremdprodukte,
  // normale Produkte ohne AVIS) werden hier nicht betrachtet.
  function groupByParent(cart) {
    var groups = {};
    (cart.items || []).forEach(function (item) {
      var pid = item.properties && item.properties[PARENT_KEY];
      if (!pid) return;
      if (!groups[pid]) groups[pid] = { main: [], charge: [] };
      var role = item.properties[ROLE_KEY];
      if (role === 'charge') groups[pid].charge.push(item);
      else groups[pid].main.push(item);
    });
    return groups;
  }

  function reconcile() {
    if (isSyncing) return Promise.resolve();
    isSyncing = true;

    return fetchCart().then(function (cart) {
      var groups = groupByParent(cart);
      var actions = [];

      Object.keys(groups).forEach(function (pid) {
        var g = groups[pid];

        if (g.main.length === 0 && g.charge.length > 0) {
          // Hauptprodukt vollstaendig entfernt -> verwaiste Aufpreiszeilen mitentfernen.
          g.charge.forEach(function (c) { actions.push({ key: c.key, quantity: 0 }); });
          return;
        }
        if (g.charge.length === 0) return; // nur Hauptprodukt(e) in dieser Gruppe, nichts zu tun

        var targetQty = g.main.reduce(function (sum, m) { return sum + m.quantity; }, 0);
        g.charge.forEach(function (c) {
          if (c.quantity !== targetQty) actions.push({ key: c.key, quantity: targetQty });
        });
      });

      if (!actions.length) {
        isSyncing = false;
        return;
      }

      // Sequentiell abarbeiten: die Shopify Cart API vertraegt parallele
      // Schreibzugriffe auf denselben Warenkorb nicht zuverlaessig
      // (Race Conditions zwischen mehreren gleichzeitigen /cart/change.js).
      return actions.reduce(function (p, action) {
        return p.then(function () { return changeLine(action.key, action.quantity); });
      }, Promise.resolve()).then(function () {
        isSyncing = false;
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
    }).catch(function () {
      isSyncing = false;
    });
  }

  document.addEventListener('cart:change', function () { reconcile(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
